import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/auth'

// Import the full Shift interface to get day_time_deduction
import { Shift } from '@/types'

// Define the shift interface based on the actual database schema
interface ShiftData extends Shift {
  start_time: string | null
  end_time: string | null
  startTime: string | null // Add the camelCase aliases
  endTime: string | null   // Add the camelCase aliases
  applicable_days: number[] | null
}

/**
 * Hook to fetch and manage shift data
 */
export const useShifts = (defaultShifts: ShiftData[] = []) => {
  const [dbShifts, setDbShifts] = useState<ShiftData[]>([])
  const { user } = useAuth()
  
  const fetchShifts = useCallback(async () => {
    // Double-check authentication before making any database calls
    if (!user?.companyId) {
      return
    }
    
    try {
      const { data, error } = await supabase
        .from('shifts')
        .select('*')
        .eq('is_del', false) // Only fetch non-deleted shifts
        .eq('company_id', user.companyId) // Filter by company ID
        .order('name')
        
      if (error) {
        return
      }
      
      if (data && data.length > 0) {
        const formattedShifts: ShiftData[] = data.map(shift => ({
          id: shift.id,
          name: shift.name,
          code: shift.code,
          valid_from: shift.valid_from,
          valid_to: shift.valid_to,
          day_time_deduction: shift.day_time_deduction || [], // Include the detailed timing data
          is_active: shift.is_active,
          is_del: shift.is_del,
          created_by: shift.created_by,
          created_at: shift.created_at,
          updated_by: shift.updated_by,
          updated_at: shift.updated_at,
          company_id: shift.company_id,
          in_working_hours: shift.in_working_hours,
          start_time: shift.start_time,
          end_time: shift.end_time,
          startTime: shift.start_time, // Add the camelCase aliases
          endTime: shift.end_time,     // Add the camelCase aliases
          applicable_days: shift.applicable_days
        }))
        setDbShifts(formattedShifts)
      }
    } catch {
      // Error in fetchShifts; optionally handle/log
    }
  }, [user?.companyId])

  useEffect(() => {
    // Only fetch shifts if user is authenticated and has a company ID
    if (user?.companyId) {
      fetchShifts()
    } else {
      // Clear shifts if user is not authenticated
      setDbShifts([])
    }
  }, [user?.companyId, fetchShifts])
  
  // Get the shifts to use (preferring database shifts if available)
  const shifts = dbShifts.length > 0 ? dbShifts : defaultShifts
  
  // Return getter function that returns current shifts
  const getShifts = () => shifts
  
  return { shifts, getShifts, fetchShifts }
}
